import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { faresApi } from 'services/apiDigitalTicket/fares';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

import * as FaresActions from './actions';
import { FaresTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof FaresActions.listFaresRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando tarifas' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(faresApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FaresActions.listFaresSuccess({
          faresList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - listFares - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.listFaresFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listCarFare({
  payload,
}: ReturnType<typeof FaresActions.listCarFaresRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando tarifas' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(faresApi.listCarFare, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FaresActions.listCarFaresSuccess({
          carFaresList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - listCarFares - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.listCarFaresFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof FaresActions.filterFaresRequest>): any {
  try {
    const transportOperator = yield select((state) => {
      if ((payload as { useSelectedOperator: boolean })?.useSelectedOperator) {
        return state.operators.operatorSelected;
      }
      return state.transportOperators.transportOperatorSelected;
    });

    const response = yield call(faresApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FaresActions.filterFaresSuccess({
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - filterFares - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.filterFaresFailure());
    meta.promise.reject(error);
  }
}

function* get({
  payload,
}: ReturnType<typeof FaresActions.getFareRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(faresApi.get, {
      params: { ...payload, transportOperatorId: transportOperatorSelected.id },
    });

    if (response.status === 200 && response.data.id) {
      yield put(FaresActions.getFareSuccess({ fareData: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - get - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.getFareFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof FaresActions.createFareRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando tarifa' }));
    const { dispatchOnSuccess } = meta;
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(faresApi.create, {
      data: { ...payload, transportOperatorId: transportOperatorSelected.id },
    });

    if (response.status === 200) {
      yield put(FaresActions.createFareSuccess({ fareCreated: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tarifa criada com sucesso',
        }),
      );

      if (dispatchOnSuccess) {
        const { action, payload } = dispatchOnSuccess;
        yield put(action(payload));
      }
      yield put(
        FaresActions.setCurrentOperation({
          operation: 'none',
        }),
      );
      yield put(
        FaresActions.listFaresRequest({
          _limit: 9999,
          _includes: 'updaterequest,linesection',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - create - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.createFareFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createCarFare({
  payload,
  meta,
}: ReturnType<typeof FaresActions.createCarFareRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando tarifa' }));
    const { dispatchOnSuccess } = meta;
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(faresApi.createCarFare, {
      data: { ...payload, transportOperatorId: transportOperatorSelected.id },
    });

    if (response.status === 200) {
      yield put(
        FaresActions.createCarFareSuccess({ fareCreated: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tarifa criada com sucesso',
        }),
      );

      if (dispatchOnSuccess) {
        const { action, payload } = dispatchOnSuccess;
        yield put(action(payload));
      }
      yield put(
        FaresActions.setCurrentOperation({
          operation: 'none',
        }),
      );
      yield put(
        FaresActions.listCarFaresRequest({
          _limit: 9999,
          _includes: 'updaterequest,linesection',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - create - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.createCarFareFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof FaresActions.updateFareRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando tarifa' }));
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(faresApi.update, {
      data: { ...payload, transportOperatorId: transportOperatorSelected.id },
    });

    if (response.status === 200) {
      yield put(FaresActions.updateFareSuccess({ fareData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tarifa alterada com sucesso',
        }),
      );
      yield put(
        FaresActions.listFaresRequest({
          _includes: 'updaterequest,linesection',
          _limit: 9999,
        }),
      );
      yield put(
        FaresActions.setCurrentOperation({
          operation: 'none',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - update - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.updateFareFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateCarFare({
  payload,
}: ReturnType<typeof FaresActions.updateCarFareRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando tarifa' }));
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(faresApi.updateCarFare, {
      data: { ...payload, transportOperatorId: transportOperatorSelected.id },
    });

    if (response.status === 200) {
      yield put(FaresActions.updateCarFareSuccess({ fareData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tarifa alterada com sucesso',
        }),
      );
      yield put(
        FaresActions.listCarFaresRequest({
          _limit: 9999,
          _includes: 'updaterequest,linesection',
        }),
      );
      yield put(
        FaresActions.setCurrentOperation({
          operation: 'none',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - update - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.updateCarFareFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof FaresActions.deleteFareRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo tarifa' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(faresApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tarifa excluída com sucesso',
        }),
      );
      yield put(
        FaresActions.listFaresRequest({
          _includes: 'updaterequest,linesection',
          _limit: 9999,
        }),
      );
      yield put(
        FaresActions.setCurrentOperation({
          operation: 'none',
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.listFaresFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeCarFare({
  payload,
}: ReturnType<typeof FaresActions.deleteCarFareRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo tarifa' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(faresApi.deleteCarFare, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tarifa excluída com sucesso',
        }),
      );
      yield put(
        FaresActions.listCarFaresRequest({
          _limit: 9999,
          _includes: 'updaterequest,linesection',
        }),
      );
      yield put(
        FaresActions.setCurrentOperation({
          operation: 'none',
        }),
      );

      yield put(FaresActions.deleteCarFareSuccess());
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fares - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FaresActions.deleteCarFareFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const faresSagas = [
  takeLatest(FaresTypes.LIST_FARES_REQUEST, list),
  takeLatest(FaresTypes.LIST_CAR_FARES_REQUEST, listCarFare),
  takeLatest(FaresTypes.FILTER_FARES_REQUEST, filter),
  takeLatest(FaresTypes.GET_FARE_REQUEST, get),
  takeLatest(FaresTypes.CREATE_FARE_REQUEST, create),
  takeLatest(FaresTypes.CREATE_CAR_FARE_REQUEST, createCarFare),
  takeLatest(FaresTypes.UPDATE_FARE_REQUEST, update),
  takeLatest(FaresTypes.UPDATE_CAR_FARE_REQUEST, updateCarFare),
  takeLatest(FaresTypes.DELETE_FARE_REQUEST, remove),
  takeLatest(FaresTypes.DELETE_CAR_FARE_REQUEST, removeCarFare),
];
